import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { hashEmail } from '../hash';
import { changeBaseUrl, getResolvedApiUrl } from '../../apis/utils/provider';
import { getSsoUrl, login } from '../../containers/Login/LoginSlice/actionCreators';
import { setIsLoginError, setIsLoginLoading } from '../../containers/Login/LoginSlice';
import { getProbeByEmailHash, getProbeByIdpDomain } from '../../api/probes';
import type { RegionEnum } from '../../config/region';
import { serverConfigs, setRegion } from '../../config/region';
import { useAppDispatch } from '../../hooks';

export enum LoginType {
  EmailAndPassword,
  SSO,
}
export enum LoginStepEnum {
  ChooseLoginMethod,
  Credentials,
}
type LoginStep =
  | { step: LoginStepEnum.ChooseLoginMethod }
  | {
      step: LoginStepEnum.Credentials;
      loginType: LoginType;
      regions: RegionEnum[] | null;
      selectedRegion?: RegionEnum;
    };

export const regionErrorTx = 'login_select_region_error';

export const useLoginFlow = () => {
  const [loginStepState, setLoginStep] = useState<LoginStep>({
    step: LoginStepEnum.ChooseLoginMethod,
  });

  const setSelectedRegion = (region: RegionEnum) => {
    setLoginStep(prev => ({ ...prev, selectedRegion: region }));
    setRegion(region);
    changeBaseUrl(getResolvedApiUrl());
  };

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const step: LoginStep = {
      step: LoginStepEnum.Credentials,
      loginType: LoginType.EmailAndPassword,
      regions: null,
    };

    if (location.pathname === '/login/sso') {
      setLoginStep({ ...step, loginType: LoginType.SSO });
    } else if (location.pathname === '/login/user-pass') {
      setLoginStep(step);
    }
  }, [location.pathname]);

  useEffect(() => {
    const regionState =
      loginStepState.step === LoginStepEnum.Credentials &&
      (!!loginStepState.regions?.length || !!loginStepState.selectedRegion);

    if (location.pathname.includes('region') && !regionState) {
      navigate(location.pathname.split('/region')[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSsoLogin = async (email: string) => {
    dispatch(getSsoUrl(email));
  };

  const handleEmailLogin = async (email: string, password: string) => {
    const res = await dispatch(login({ email, password }));

    if (res === '2fa') {
      navigate('/2fa');
      return;
    }
  };

  const moveToSelectRegionStep = (
    availableRegions: RegionEnum[],
    loginType: LoginType,
    handleSingleRegionDirectLogin: () => void
  ) => {
    try {
      if (!availableRegions.length) {
        dispatch(setIsLoginError('login_error'));
        return;
      }
      const isSingleRegion = availableRegions.length === 1;
      const step: LoginStep = {
        step: LoginStepEnum.Credentials,
        loginType,
        regions: isSingleRegion ? null : availableRegions,
        selectedRegion: isSingleRegion ? availableRegions[0] : undefined,
      };

      setLoginStep(step);
      if (isSingleRegion) {
        setSelectedRegion(availableRegions[0]);
        handleSingleRegionDirectLogin?.();
      } else {
        dispatch(setIsLoginLoading(false));
        navigate(`${location.pathname}/region`, {
          state: { from: location.state?.from },
        });
      }
    } catch (error) {
      dispatch(setIsLoginLoading(false));
      console.error(error);
    }
  };

  const handleGatherRegionsEmail = async (email: string, password: string) => {
    dispatch(setIsLoginLoading(true));
    let continueLoadingForDirectLogin = false;
    try {
      const emailHash = await hashEmail(email ?? '');
      const allServersUrls = Object.values(serverConfigs).map(server => server.coreApiUrl);
      const availabilityPromises = allServersUrls.map(server =>
        getProbeByEmailHash({ hash: emailHash, password, regionBaseUrl: server })
      );

      const results = await Promise.allSettled(availabilityPromises);

      const fulfilledResults = results.filter(
        (
          result
        ): result is PromiseFulfilledResult<Awaited<ReturnType<typeof getProbeByEmailHash>>> =>
          result.status === 'fulfilled'
      );
      const availableRegions = fulfilledResults
        .map(res => res.value)
        .filter(res => !res.error && !!res.data)
        .filter(res => res.data?.data.available)
        .map(res => res.data?.data.region)
        .filter((region): region is RegionEnum => Boolean(region));

      continueLoadingForDirectLogin = availableRegions.length === 1;
      moveToSelectRegionStep(availableRegions, LoginType.EmailAndPassword, () =>
        handleEmailLogin(email, password)
      );
    } catch (_error) {
      console.error('Error checking availability:', _error);
      dispatch(setIsLoginLoading(false));
      dispatch(
        setIsLoginError(_error instanceof Error ? _error.message : 'An unknown error occurred')
      );
    } finally {
      if (!continueLoadingForDirectLogin) dispatch(setIsLoginLoading(false));
    }
  };

  const handleGatherRegionsSSO = async (email: string) => {
    dispatch(setIsLoginLoading(true));
    let continueLoadingForDirectLogin = false;
    try {
      const idpDomain = email.split('@')[1];
      const allServersUrls = Object.values(serverConfigs).map(server => server.coreApiUrl);
      const availabilityPromises = allServersUrls.map(server =>
        getProbeByIdpDomain({ idpDomain, regionBaseUrl: server })
      );
      const results = await Promise.allSettled(availabilityPromises);

      const fulfilledResults = results.filter(
        (
          result
        ): result is PromiseFulfilledResult<Awaited<ReturnType<typeof getProbeByIdpDomain>>> =>
          result.status === 'fulfilled'
      );
      const availableRegions = fulfilledResults
        .map(res => res.value)
        .filter(res => !res.error && !!res.data)
        .filter(res => res.data?.data.available)
        .map(res => res.data?.data.region)
        .filter((region): region is RegionEnum => Boolean(region));

      continueLoadingForDirectLogin = availableRegions.length === 1;
      moveToSelectRegionStep(availableRegions, LoginType.SSO, () => handleSsoLogin(email));
    } catch (_error) {
      console.error('Error checking availability:', _error);
      dispatch(
        setIsLoginError(_error instanceof Error ? _error.message : 'An unknown error occurred')
      );
      dispatch(setIsLoginLoading(false));
    } finally {
      if (!continueLoadingForDirectLogin) dispatch(setIsLoginLoading(false));
    }
  };

  const handleGatheringRegionStep = async (
    loginType: LoginType,
    email: string,
    password?: string
  ) => {
    if (loginType === LoginType.EmailAndPassword) {
      if (!password) {
        dispatch(setIsLoginError('login_error'));
        return;
      }
      await handleGatherRegionsEmail(email, password);
    } else {
      await handleGatherRegionsSSO(email);
    }
  };

  const handleCredentialsStep = async (
    email: string,
    password: string,
    loginType?: LoginType,
    selectedRegion?: RegionEnum
  ) => {
    if (!selectedRegion) {
      dispatch(setIsLoginError(regionErrorTx));
      return;
    }
    if (loginType === LoginType.EmailAndPassword) {
      if (!password) {
        dispatch(setIsLoginError('login_error'));
        return;
      }
      await handleEmailLogin(email, password);
      return;
    }
    await handleSsoLogin(email);
  };

  const handleSubmit = async (email: string, password: string) => {
    const haveRegionsLoaded = 'regions' in loginStepState && !!loginStepState.regions?.length;
    const loginType =
      'loginType' in loginStepState ? loginStepState.loginType : LoginType.EmailAndPassword;
    const selectedRegion =
      'selectedRegion' in loginStepState ? loginStepState.selectedRegion : undefined;
    if (!haveRegionsLoaded) {
      return await handleGatheringRegionStep(loginType, email, password);
    } else {
      return await handleCredentialsStep(email, password, loginType, selectedRegion);
    }
  };

  return {
    loginStepState,
    setLoginStep,
    setSelectedRegion,
    handleSubmit,
  };
};
